import { useMemo } from "react";
import { AverageRatingChart } from "../player/PlayerStats/AverageRatingChart";
import { performanceCards } from "./utils/performanceIcons";
import { formatStatCount } from "./utils/playerStatsUtils";
import { chunkSections, StatsSection } from "./StatsSection";
import {
  StatsHeaderBar,
  PerformanceCards,
  StatsSkeleton,
} from "./player-stats";
import { useStatsData, useFilteredPerformanceCards } from "./hooks";

const PlayerStatsPlayer = ({
  seasonStats,
  seasonOptions,
  selectedSeasonId,
  setSelectedSeasonId,
  statsLoading,
  ratingChartData,
  selectedMatch,
  ratingsLoading,
  ratingsError,
}) => {
  // Use shared hook for stats data extraction with goalkeeper filtering
  const {
    blocks: filteredBlocks,
    teamName: displayTeamName,
    teamLogo: displayTeamImage,
    seasonName: displaySeasonName,
    stats,
    startsValue,
    appearancesValue,
    shouldShowStartsAppearances,
    shouldShowAppearancesCard,
    hasStats: hasStatsData,
  } = useStatsData(seasonStats, { filterGoalkeeper: true });


  // Filter performance cards based on validation rules
  const filteredPerformanceCards = useFilteredPerformanceCards(
    performanceCards,
    shouldShowAppearancesCard
  );

  // Sort stat blocks to control column placement (left: Overview/Pass/Defence, right: Shots/Drib-Possession)
  const sortedBlocks = useMemo(() => {
    const order = {
      Overview: 0,
      Pass: 1,
      Defence: 2,
      Shots: 3,
      "Drib-Possession": 4,
      Goalkeeper: 5,
    };
    return [...filteredBlocks].sort((a, b) => {
      const aPos = order[a?.name] ?? 99;
      const bPos = order[b?.name] ?? 99;
      return aPos - bPos;
    });
  }, [filteredBlocks]);

  // Split blocks into two columns without forcing equal heights
  const columns = useMemo(() => {
    const leftNames = new Set(["Overview", "Pass", "Defence"]);
    const rightNames = new Set(["Shots", "Drib-Possession"]);
    const left = [];
    const right = [];

    sortedBlocks.forEach((block) => {
      if (leftNames.has(block?.name)) {
        left.push(block);
      } else if (rightNames.has(block?.name)) {
        right.push(block);
      } else {
        // Fallback: balance by shorter column
        if (left.length <= right.length) left.push(block);
        else right.push(block);
      }
    });

    return { left, right };
  }, [sortedBlocks]);

  // Get minutes from stats
  const minutesValue = useMemo(() => {
    const block = filteredBlocks.find((b) => b.name === "Overview");
    const stat = block?.stats?.find((s) => s.key === "minutes_played");
    return stat?.value || 0;
  }, [filteredBlocks]);

  // Check if stats are empty
  const hasStats = !statsLoading && hasStatsData;

  // Build section items for stats display
  const buildSectionItems = (block) => {
    if (!block?.stats) {
      return [];
    }

    // Dynamic item creation:
    // - If value is a number/string: show directly
    // - If value is an object: expand into multiple rows with `${label} ${subKey}`
    return block.stats.flatMap((stat) => {
      const value = stat?.value;
      const base = {
        key: stat?.key,
        rawValue: value,
      };

      if (value !== null && typeof value === "object") {
        return Object.entries(value).map(([subKey, subVal]) => {
          const displayVal = formatStatCount({ [subKey]: subVal }, [subKey]);
          // If label and subKey are the same (case-insensitive), don't append
          const labelMatches =
            stat?.label?.toLowerCase() === subKey?.toLowerCase();
          const label = labelMatches ? stat?.label : `${stat?.label} ${subKey}`;
          return {
            ...base,
            label,
            value: displayVal,
          };
        });
      }

      const displayVal = formatStatCount({ [stat?.key]: value }, [stat?.key]);
      return [
        {
          ...base,
          label: stat?.label,
          value: displayVal,
        },
      ];
    });
  };

  return (
    <div className="w-full mx-auto py-6 bg-white space-y-6">
      {/* Stats sections - Only show if stats are available */}
      {statsLoading ? (
        <StatsSkeleton
          ratingChartData={ratingChartData}
          statsLoading={statsLoading}
          ratingsLoading={ratingsLoading}
          selectedMatch={selectedMatch}
        />
      ) : hasStats ? (
        <>
          {/* Header Info Bar — prop names below are what StatsHeaderBar
              actually destructures (starts/appearances/minutes/touches/
              showStartsAppearances). The previous *Value suffix variants
              fell through to the default `= 0` parameters and silently
              rendered the header as "0 starts · 0 appearances · 0 mins ·
              0 touches" even when the backend returned full stats. */}
          <StatsHeaderBar
            teamLogo={displayTeamImage}
            teamName={displayTeamName}
            seasonName={displaySeasonName}
            starts={startsValue}
            appearances={appearancesValue}
            minutes={minutesValue}
            touches={0}
            showStartsAppearances={shouldShowStartsAppearances}
          />

          {/* Performance Summary Cards */}
          <PerformanceCards cards={filteredPerformanceCards} stats={stats} />

          {/* Average Rating Chart - Always visible */}
          <AverageRatingChart
            chartData={ratingChartData}
            isLoading={statsLoading || ratingsLoading}
            selectedMatch={selectedMatch}
          />

          <div className="flex flex-col gap-6 md:flex-row md:items-start md:gap-6">
            <div className="flex-1 flex flex-col gap-6">
              {columns.left.map((block) => (
                <StatsSection
                  key={block.name}
                  title={block.name}
                  items={buildSectionItems(block)}
                />
              ))}
            </div>

            <div className="flex-1 flex flex-col gap-6">
              {columns.right.map((block) => (
                <StatsSection
                  key={block.name}
                  title={block.name}
                  items={buildSectionItems(block)}
                />
              ))}
            </div>
          </div>
        </>
      ) : null}

      {/* Average Rating Chart - Always visible (when no stats) */}
      {!statsLoading && !hasStats && (
        <AverageRatingChart
          chartData={ratingChartData}
          isLoading={statsLoading || ratingsLoading}
          selectedMatch={selectedMatch}
        />
      )}
    </div>
  );
};

export default PlayerStatsPlayer;
